import { AliceSkillSettings } from 'client/model/settings';
import { UserAgreementOrDraft } from 'client/model/userAgreement';
import * as regionsKeyset from 'i18n/Region';

import { AliceApp, AliceSkill, Channel, InstallableApp, Owner, Skill, SmartHome } from '../model/skill';

import { YANDEX_COUNTRIES_SET } from './countries';

export const isIframe = () => window.self !== window.parent;

export const hrefIsExternal = new RegExp('^(?:[a-z]+:)?//', 'i');

export const popupWindow = (url: string, title: string, w: number, h: number) => {
    const left = screen.width / 2 - w / 2;
    const top = screen.height / 2 - h / 2;

    return window.open(
        url,
        title,
        `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${top}, left=${left}`,
    );
};

export function assignField<T, K extends string | number | symbol, V>(obj: T, field: K, value: V) {
    return Object.assign({}, obj, { [field]: value });
}

export function getKeys<T extends Record<string, unknown>>(obj: T) {
    return Object.keys(obj) as Array<keyof T>;
}

export function selectTextContent(domNode: HTMLElement) {
    const selection = window.getSelection();

    const range = document.createRange();
    range.selectNodeContents(domNode);

    selection!.removeAllRanges();
    selection!.addRange(range);
}

export function makeSafe<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
    return async (...args: A) => {
        try {
            return await fn(...args);
        } catch (e) {
            console.error(e);
        }
    };
}

export const ucfirst = (s: string) => s[0].toUpperCase() + s.slice(1);

export const getSettingsEntity = <T extends AliceApp>(aliceApp: T, isDraft: boolean) => {
    return isDraft ? (aliceApp.draft as T['draft']) : aliceApp;
};

export const getSettingsWithUserAgreements = (
    aliceApp: AliceSkill | SmartHome,
    isDraft: boolean,
    userAgreements?: UserAgreementOrDraft[],
): AliceSkillSettings => {
    const settings = isDraft ? aliceApp.draft : aliceApp;
    return { ...settings, skillUserAgreements: userAgreements || [] };
};

export const getInstallableAppSettingsEntity = <T extends InstallableApp>(installableApp: T, isDraft: boolean) => {
    // ad-hoc: settings are propagated to <Settings> component, but for tvApp.draft we should also
    // propagate skill.packageName (because it should be modifiable until the first publication)
    if (isDraft) {
        return {
            ...installableApp.draft,
            packageName: (installableApp as InstallableApp).packageName,
        } as T['draft'] & { packageName: string };
    }
    return installableApp;
};

const showAppsUi = () => Boolean(__appConfig.user.featureFlags.enableAppsAllChannels);
export const showAppStoreUi = () => !getIsYangoRequest();
export const showAutoAppUi = () =>
    (Boolean(__appConfig.user.featureFlags.enableAutoApps) && showAppStoreUi()) || showAppsUi();

/** Доступ к разделу "Alice Apps". IDM-разработчикам и админам — всегда. Гасится серверным килсвитчем для всех. */
export const canUseAliceApps = () =>
    !__appConfig.app.disableAliceAppsSection &&
    (Boolean(__appConfig.user.featureFlags.allowUseAliceApps) ||
        Boolean(__appConfig.user.canCreateAliceInternalAgentApp) ||
        Boolean(__appConfig.user.canManageAliceAgentAppAccess) ||
        Boolean(__appConfig.user.hasAliceAgentAppAdminAccess) ||
        __appConfig.user.isAdmin);

/** Может назначать и отзывать администраторов конкретных Alice Agent App по GUID. */
export const canManageAliceAgentAppAccess = () =>
    !__appConfig.app.disableAliceAppsSection && Boolean(__appConfig.user.canManageAliceAgentAppAccess);

/** Создание новых внутренних агентов. На уже созданных не влияет — они всегда видны владельцу. */
export const canCreateAliceInternalAgentApp = () =>
    !__appConfig.app.disableAliceAppsSection &&
    (Boolean(__appConfig.user.featureFlags.allowAliceInternalAgentApp) ||
        Boolean(__appConfig.user.canCreateAliceInternalAgentApp) ||
        __appConfig.user.isAdmin);

/** Создание новых внешних агентов (MCP). На уже созданных не влияет — они всегда видны владельцу. */
export const canCreateAliceExternalApp = () =>
    !__appConfig.app.disableAliceAppsSection &&
    (Boolean(__appConfig.user.featureFlags.allowAliceExternalApp) || __appConfig.user.isAdmin);

/** Store metadata fields are enabled per current user, with an admin override. */
export const canUseAliceAgentAppCategory = () =>
    Boolean(__appConfig.user.featureFlags.allowAliceAgentAppCategory) || getIsAdmin();

export const canUseAliceAgentAppCover = () =>
    Boolean(__appConfig.user.featureFlags.allowAliceAgentAppCover) || getIsAdmin();

export const getIsYangoRequest = () => Boolean(__appConfig.isYangoRequest);

export const getQuasarUiUrl = () =>
    getIsYangoRequest() ? __appConfig.app.quasarUiYangoUrl : __appConfig.app.quasarUiUrl;

export const removeLeadingSlash = (value: string) => {
    return value.replace(/^\//, '');
};

export const shouldMarkAsProvidedByOtherCompany = (countryCode: keyof (typeof regionsKeyset)['en']) => {
    return (
        (getIsYangoRequest() && YANDEX_COUNTRIES_SET.has(countryCode)) ||
        (!getIsYangoRequest() && !YANDEX_COUNTRIES_SET.has(countryCode))
    );
};

export const isInstallableAppChannel = (
    channel: Channel,
): channel is Channel.AutoApp | Channel.TvApp | Channel.SmartScreenApp => {
    return channel === Channel.TvApp || channel === Channel.AutoApp || channel === Channel.SmartScreenApp;
};

export const isAliceSkillOrSmartHomeSkill = (skill: Skill): skill is AliceSkill | SmartHome =>
    skill.channel === Channel.AliceSkill || skill.channel === Channel.SmartHome;

export const isSmartHomeSkill = (skill: Skill): skill is SmartHome => skill.channel === Channel.SmartHome;

export const isInstallableApp = (app: InstallableApp | Skill): app is InstallableApp => {
    return isInstallableAppChannel(app.channel);
};

export const getIsAdmin = () => __appConfig.user?.isAdmin;
export const getIsGlobalViewer = () => !!__appConfig.user?.isGlobalViewer;
export const getAccessAsViewer = () => getIsAdmin() || getIsGlobalViewer();

export const getCanViewAsAdmin = () => getIsAdmin() || getIsGlobalViewer();
export const getCanEditAsAdmin = () => getIsAdmin();

export const getCanEditApp = (app: AliceApp) => {
    const appUserId = app?.user?.id || app?.userId;
    const isCurrentAppOwnerCommonUser = __appConfig.user.blackbox.uid === appUserId;
    const isCurrentAppOwnerPorgUser =
        app?.owner?.type === Owner.PORG && __appConfig.user.porgId === app.owner?.entityId;

    return isCurrentAppOwnerCommonUser || isCurrentAppOwnerPorgUser || getIsAdmin();
};

export const getIsUserPorg = () => Boolean(__appConfig.user?.porgName);

export const displayUserAgreements = () => __appConfig.user.featureFlags.enableUserAgreements || getCanViewAsAdmin();
